import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './VerificacaoAndaime.css';

/* ============================================================
   MANUAIS — guias rápidos para os trabalhadores (acesso mobile)
   Cada manual tem 2 páginas A4: os da aplicação com capturas do próprio
   telemóvel, o dos EPI com os pictogramas de uso obrigatório.
   Os ficheiros estão em public/manuais/<id>: o PDF, para imprimir
   ou partilhar, e uma imagem JPG por página, para ler no telemóvel.
   ============================================================ */
const MANUAIS = [
    {
        id: 'registo-incidentes', icone: '🚑', titulo: 'Incidentes e comunicações',
        texto: 'Comunicar um acidente, quase-acidente, condição perigosa, reclamação, não conformidade ou sugestão.',
    },
    {
        id: 'registo-ferramentas', icone: '🛠️', titulo: 'Registar ferramentas e RMM',
        texto: 'Dar entrada de uma ferramenta ou de um equipamento de medição, com marca, modelo e nº de série.',
    },
    {
        id: 'produtos-quimicos-fds', icone: '🧪', titulo: 'Produtos químicos e FDS',
        texto: 'Consultar a ficha de dados de segurança de um produto e registar produtos novos.',
    },
    {
        id: 'rotulos-produtos-quimicos', icone: '⚠️', titulo: 'Rótulos de produtos químicos',
        texto: 'Ler o rótulo e os pictogramas de perigo, o que fazer em caso de contacto e onde estão as FDS e as fichas técnicas.',
    },
    {
        id: 'utilizacao-epi', icone: '🦺', titulo: 'EPI: como usar e quando',
        texto: 'Que equipamento de protecção vestir em cada trabalho, como verificá-lo e quando pedir a substituição.',
    },
];
const PAGINAS = 2;
const urlPdf = (m) => `/manuais/${m.id}.pdf`;
const urlPagina = (m, n) => `/manuais/${m.id}-${n}.jpg`;

const ManuaisMobile = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [aberto, setAberto] = useState(null); // id do manual com as páginas à vista

    const voltar = () => (user?.perfil_mobile ? navigate('/mobile-dashboard') : navigate(-1));

    return (
        <div className="verificacao-andaimes-container">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1>📘 Manuais</h1>
                        <p className="mobile-hide">Guias rápidos da SPROD</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    <button className="btn-secondary" style={{ minHeight: 44, whiteSpace: 'nowrap' }} onClick={voltar}>
                        {user?.perfil_mobile ? '← Menu' : '← Voltar'}
                    </button>
                </div>
            </div>

            <div className="page-content" style={{ padding: 16 }}>
                <div className="va-content-wrapper">
                    <p style={intro}>
                        Guias rápidos, ilustrados passo a passo. Toque em <b>Ver o manual</b> para o ler aqui,
                        ou abra o <b>PDF</b> para imprimir ou enviar.
                    </p>

                    {MANUAIS.map(m => {
                        const ver = aberto === m.id;
                        return (
                            <div key={m.id} style={cartao}>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                    <span style={icone} aria-hidden="true">{m.icone}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={titulo}>{m.titulo}</div>
                                        <div style={texto}>{m.texto}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                    <button type="button" aria-expanded={ver} onClick={() => setAberto(ver ? null : m.id)} style={btnVer(ver)}>
                                        {ver ? '▲ Fechar' : '👁️ Ver o manual'}
                                    </button>
                                    <a href={urlPdf(m)} target="_blank" rel="noopener noreferrer" style={btnPdf}
                                        aria-label={`${m.titulo} — abrir o PDF`}>
                                        📄 PDF
                                    </a>
                                </div>
                                {ver && (
                                    <div style={{ marginTop: 12 }}>
                                        {Array.from({ length: PAGINAS }, (_, i) => (
                                            <a key={i} href={urlPagina(m, i + 1)} target="_blank" rel="noopener noreferrer"
                                                aria-label={`Página ${i + 1} de ${PAGINAS} — abrir em tamanho grande`}>
                                                <img src={urlPagina(m, i + 1)} alt={`${m.titulo} — página ${i + 1}`} loading="lazy" style={pagina} />
                                            </a>
                                        ))}
                                        <div style={dica}>Toque numa página para a abrir em tamanho grande e ampliar.</div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ManuaisMobile;

/* ---------- estilos ---------- */
const intro = { fontSize: 14, color: '#4a5568', lineHeight: 1.45, margin: '0 0 14px' };
const cartao = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
const icone = { fontSize: 30, lineHeight: 1, flexShrink: 0 };
const titulo = { fontWeight: 700, fontSize: 16, color: '#1a202c', lineHeight: 1.3 };
const texto = { fontSize: 14, color: '#4a5568', lineHeight: 1.4, marginTop: 3 };
const btnVer = (ativo) => ({
    flex: 1, minHeight: 48, borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit',
    border: `2px solid ${ativo ? '#cbd5e0' : '#2b6cb0'}`, background: ativo ? '#fff' : '#2b6cb0', color: ativo ? '#2d3748' : '#fff',
});
const btnPdf = {
    minHeight: 48, minWidth: 96, padding: '0 14px', borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
    border: '2px solid #cbd5e0', background: '#fff', color: '#2d3748',
};
const pagina = { display: 'block', width: '100%', height: 'auto', borderRadius: 8, border: '1px solid #cbd5e0', marginBottom: 10, background: '#fff' };
const dica = { fontSize: 12, color: '#718096', textAlign: 'center' };
